import { QueryClient } from '@tanstack/react-query';

/**
 * The single TanStack Query client shared across the console.
 *
 * It is handed to Refine via `options.reactQuery.clientConfig` (Refine reuses a
 * `QueryClient` instance as-is rather than wrapping it), so every Refine hook
 * (`useList`, `useOne`, …) AND every ad-hoc `useQuery` in the shared screens
 * runs against THIS client.
 *
 * Because {@link CompanyScopeProvider} is mounted ABOVE `<Refine>` (it feeds the
 * company list into Refine's resources), a hook-based `useQueryClient()` has no
 * provider there. Sharing this concrete instance is what lets the provider call
 * `queryClient.invalidateQueries()` on every scope change and have it reach the
 * queries Refine renders below it.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A scoped list should refetch on exactly one thing: a tenant-scope
      // switch, which {@link CompanyScopePicker} drives explicitly via
      // `invalidateQueries()`. React Query's defaults (staleTime 0 +
      // refetchOnWindowFocus) also refetch whenever a "stale" query is
      // re-subscribed or the window regains focus — which fired on ordinary
      // interactions (opening a column filter re-rendered the list, re-running
      // the instantly-stale query) and blanked the table's data for a frame:
      // the DataTable filter flicker. A short staleTime + no refetch-on-focus
      // removes those spurious refetches without touching the explicit
      // scope-switch invalidation (invalidate marks stale + refetches
      // regardless of staleTime).
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});
